import { ReactNode } from 'react';
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';

import useIsTruncated from '../../hooks/useIsTruncated';

// Shared card dimensions, used by TaskCard and TaskGroupCard.
const CARD_WIDTH = { xs: 288, md: 336 };
const CARD_MIN_HEIGHT = 144;

export interface TaskCardSecondaryCta {
    label: string;
    onClick: () => void;
    disabled?: boolean;
}

interface TaskCardShellProps {
    title: string;
    ctaLabel: string;
    onCtaClick: () => void;
    /** A second choice beside the main one, for tasks the member can resolve without the flow. */
    secondaryCta?: TaskCardSecondaryCta | null;
    dateBadge?: ReactNode;
    description?: string;
    overdue?: boolean;
    /** Feed exit animation is playing: flash the surface green before it collapses away. */
    isCompleting?: boolean;
}

function CtaButton({ ctaLabel, onCtaClick }: Readonly<{ ctaLabel: string; onCtaClick: () => void }>) {
    return (
        <Button
            variant="outlined"
            color="grey"
            size="small"
            fullWidth
            onClick={onCtaClick}
            sx={{ color: 'text.primary', bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}>
            {ctaLabel}
        </Button>
    );
}

function OverdueCta({ children }: Readonly<{ children: ReactNode }>) {
    return (
        <Box sx={{ bgcolor: 'grey.50', borderRadius: 3 }}>
            <Typography variant="overline" color="error" align="center" sx={{ display: 'block', py: 0.5 }}>
                Overdue
            </Typography>
            {children}
        </Box>
    );
}

/**
 * Presentational layout shared by TaskCard and TaskGroupCard: fixed-size Paper with a
 * date-badge + title/description header and a full-width CTA. Owns the visual contract including
 * the two-line description clamp + tooltip; each card supplies its own data and click behavior.
 */
export default function TaskCardShell({
    title,
    ctaLabel,
    onCtaClick,
    secondaryCta = null,
    dateBadge,
    description,
    overdue = false,
    isCompleting = false,
}: Readonly<TaskCardShellProps>) {
    // With a second choice the main one is the action we want members to take, so it gets the
    // primary weight and the alternative sits under it as a quieter text button.
    const ctas = secondaryCta ? (
        <Stack spacing={0.5}>
            <Button variant="contained" size="small" fullWidth onClick={onCtaClick}>
                {ctaLabel}
            </Button>
            <Button
                variant="text"
                color="grey"
                size="small"
                fullWidth
                disabled={secondaryCta.disabled}
                onClick={secondaryCta.onClick}
                sx={{ color: 'text.secondary' }}>
                {secondaryCta.label}
            </Button>
        </Stack>
    ) : (
        <CtaButton ctaLabel={ctaLabel} onCtaClick={onCtaClick} />
    );

    // Only show the full-text tooltip when the 2-line clamp actually truncates the description.
    const { ref: descriptionRef, isTruncated } = useIsTruncated(description ?? '');

    return (
        <Paper
            data-mobile-narrow
            sx={{
                width: CARD_WIDTH,
                flexShrink: 0,
                minHeight: CARD_MIN_HEIGHT,
                p: 2,
                borderRadius: 4,
                display: 'flex',
                flexDirection: 'column',
                // The feed exit animation flashes the surface green before it collapses away.
                ...(isCompleting && { backgroundColor: 'success.lightHover' }),
            }}>
            <Stack sx={{ flex: 1, gap: 3 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    {dateBadge}
                    <Stack sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                        <Typography variant="h5">{title}</Typography>
                        {description && (
                            <Tooltip title={description} placement="top" disableHoverListener={!isTruncated}>
                                <Typography
                                    ref={descriptionRef}
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}>
                                    {description}
                                </Typography>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>
                <Stack sx={{ mt: 'auto' }}>{overdue ? <OverdueCta>{ctas}</OverdueCta> : ctas}</Stack>
            </Stack>
        </Paper>
    );
}
